import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Modal } from "@/pages/Employees";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toastWithUndo } from "@/lib/undo";

const empty = { name: "", location_id: "", description: "" };

export default function Teams() {
  const { t } = useI18n();
  const [teams, setTeams] = useState([]);
  const [locations, setLocations] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = async () => {
    const [tm, l] = await Promise.all([api.get("/teams"), api.get("/locations")]);
    setTeams(tm.data); setLocations(l.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setForm({ ...empty, location_id: locations[0]?._id || "" }); setShow(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t }); setShow(true); };
  const save = async () => {
    try {
      const payload = { ...form, location_id: form.location_id || null };
      if (editing) await api.patch(`/teams/${editing._id}`, payload);
      else await api.post("/teams", payload);
      toast.success(t("saved")); setShow(false); load();
    } catch { toast.error(t("error")); }
  };
  const del = (x) => setConfirmDel(x);
  const doDelete = async () => {
    const x = confirmDel;
    setConfirmDel(null);
    if (!x) return;
    const snapshot = { name: x.name, location_id: x.location_id || null, description: x.description || "" };
    await api.delete(`/teams/${x._id}`);
    load();
    toastWithUndo({
      message: t("team_deleted"),
      undoLabel: t("undo"),
      onUndo: async () => { await api.post("/teams", snapshot); load(); },
    });
  };
  const locName = (id) => locations.find((l) => l._id === id)?.name || "—";

  return (
    <div data-testid="page-teams">
      <PageHeader
        eyebrow={t("teams")}
        title={t("teams")}
        actions={<button onClick={openNew} className="btn btn-primary" data-testid="add-team"><Plus size={14} />{t("add_team")}</button>}
      />
      {teams.length === 0 ? <EmptyState /> : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b hairline"><tr className="text-left">
              <th className="p-3 eyebrow">{t("name")}</th>
              <th className="p-3 eyebrow">{t("location")}</th>
              <th className="p-3 eyebrow">{t("description")}</th>
              <th className="p-3 eyebrow text-right"></th>
            </tr></thead>
            <tbody>
              {teams.map((tm) => (
                <tr key={tm._id} className="border-t hairline hover:bg-[hsl(var(--muted))]" data-testid={`team-${tm._id}`}>
                  <td className="p-3 font-semibold">{tm.name}</td>
                  <td className="p-3">{locName(tm.location_id)}</td>
                  <td className="p-3 text-[hsl(var(--text-muted))]">{tm.description || "—"}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(tm)} className="btn btn-ghost !h-8" data-testid={`edit-team-${tm._id}`}><Pencil size={12} /></button>
                      <button onClick={() => del(tm)} className="btn btn-ghost !h-8" data-testid={`del-team-${tm._id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={!!confirmDel}
        title={t("confirm_delete_title")}
        body={confirmDel ? t("confirm_delete_team").replace("{name}", confirmDel.name) : ""}
        confirmLabel={t("delete")}
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
        testid="confirm-team"
      />
      {show && (
        <Modal title={editing ? t("edit") : t("add_team")} onClose={() => setShow(false)}>
          <div className="flex flex-col gap-3">
            <F label={t("name")}><input data-testid="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></F>
            <F label={t("location")}>
              <select value={form.location_id || ""} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className={inp}>
                <option value="">—</option>
                {locations.map((l) => (<option key={l._id} value={l._id}>{l.name}</option>))}
              </select>
            </F>
            <F label={t("description")}><textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp + " !h-auto py-2"} /></F>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setShow(false)} className="btn btn-ghost">{t("cancel")}</button>
            <button onClick={save} className="btn btn-primary" data-testid="team-save">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
const inp = "card h-10 px-3 text-sm rounded-xl w-full";
function F({ label, children }) { return (<label className="flex flex-col gap-1"><span className="eyebrow">{label}</span>{children}</label>); }
